import { useCallback, useEffect, useRef, useState } from "react";
import { api, setUnauthorizedHandler, type AuthState } from "./api";
import type { BriefSummary, RunStatus } from "./types";
import { BriefList } from "./components/BriefList";
import { BriefReader } from "./components/BriefReader";
import { ChatPanel } from "./components/ChatPanel";
import { SettingsModal } from "./components/SettingsModal";
import { Login } from "./components/Login";

export function App() {
  const [briefs, setBriefs] = useState<BriefSummary[]>([]);
  const [briefsLoading, setBriefsLoading] = useState(false);
  const [briefsError, setBriefsError] = useState<string | null>(null);

  const [selected, setSelected] = useState<BriefSummary | null>(null);
  const [content, setContent] = useState<string | null>(null);
  const [contentLoading, setContentLoading] = useState(false);
  const [contentError, setContentError] = useState<string | null>(null);

  const [settingsOpen, setSettingsOpen] = useState(false);

  const [runStatus, setRunStatus] = useState<RunStatus | null>(null);
  const [triggering, setTriggering] = useState(false);
  const wasRunning = useRef(false);

  const [auth, setAuth] = useState<AuthState | null>(null);
  const authed = auth ? !auth.enabled || auth.authenticated : false;

  const loadBriefs = useCallback(async (selectFirst = false) => {
    setBriefsLoading(true);
    setBriefsError(null);
    try {
      const list = await api.listBriefs();
      setBriefs(list);
      if (selectFirst && list.length > 0 && !selected) {
        void openBrief(list[0]);
      }
    } catch (err) {
      setBriefsError((err as Error).message);
    } finally {
      setBriefsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  const openBrief = useCallback(async (brief: BriefSummary) => {
    setSelected(brief);
    setContent(null);
    setContentLoading(true);
    setContentError(null);
    void api.postEvent(`Opened brief ${brief.filename}.`);
    try {
      setContent(await api.briefContent(brief.id));
    } catch (err) {
      setContentError((err as Error).message);
    } finally {
      setContentLoading(false);
    }
  }, []);

  // Check auth once on load, and register the 401 handler so an expired session
  // drops us back to the login screen.
  useEffect(() => {
    setUnauthorizedHandler(() =>
      setAuth((a) => ({ enabled: true, authenticated: false, user: a?.user ?? null })),
    );
    api
      .authStatus()
      .then(setAuth)
      .catch(() => setAuth({ enabled: true, authenticated: false, user: null }));
  }, []);

  useEffect(() => {
    if (!authed) return;
    void loadBriefs(true);
    // Poll for newly-generated briefs so the list picks them up without reload.
    const t = window.setInterval(() => void loadBriefs(false), 30_000);
    return () => window.clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed]);

  // Poll generation status so the header shows a live indicator (covers cron
  // runs and Settings-triggered runs too). Refresh briefs when a run finishes.
  useEffect(() => {
    if (!authed) return;
    let alive = true;
    const poll = async () => {
      try {
        const s = await api.runStatus();
        if (!alive) return;
        setRunStatus(s);
        if (wasRunning.current && !s.running) {
          // A run just finished — pull in the new brief.
          void loadBriefs(false);
        }
        wasRunning.current = s.running;
      } catch {
        /* transient; keep last known status */
      }
    };
    void poll();
    const t = window.setInterval(poll, 3000);
    return () => {
      alive = false;
      window.clearInterval(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed]);

  const logout = useCallback(async () => {
    try {
      await api.logout();
    } catch {
      /* ignore */
    }
    setAuth({ enabled: true, authenticated: false, user: null });
    setSelected(null);
    setContent(null);
    setBriefs([]);
  }, []);

  const generate = useCallback(async () => {
    setTriggering(true);
    void api.postEvent("Generate now clicked.");
    try {
      await api.triggerRun();
      const s = await api.runStatus();
      setRunStatus(s);
      wasRunning.current = s.running;
    } catch (err) {
      void api.postEvent("Generate trigger failed.", "error", { message: (err as Error).message });
    } finally {
      setTriggering(false);
    }
  }, []);

  const running = Boolean(runStatus?.running) || triggering;
  const lastFailed = !running && runStatus?.lastRunStatus === "failed";

  if (!auth) {
    return <div className="loading-screen">Loading…</div>;
  }
  if (auth.enabled && !auth.authenticated) {
    return <Login onSuccess={(user) => setAuth({ enabled: true, authenticated: true, user })} />;
  }

  return (
    <div className="app">
      <header className="app-header">
        <div className="brand">
          <span className="logo">◆</span> Daily Brief
        </div>
        <div className="header-actions">
          {running ? (
            <span className="run-chip running" title="A brief is being generated">
              <span className="spinner" /> Generating…
            </span>
          ) : (
            <>
              {lastFailed && (
                <span className="run-chip failed" title="Last generation failed — see Settings → Debug Log">
                  ⚠ Last run failed
                </span>
              )}
              {runStatus?.nextRunAt && (
                <span className="run-next" title={`Next scheduled run: ${new Date(runStatus.nextRunAt).toLocaleString()}`}>
                  next {new Date(runStatus.nextRunAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </span>
              )}
              <button className="secondary" onClick={() => void generate()}>
                ⚡ Generate now
              </button>
            </>
          )}
          <button className="secondary" onClick={() => setSettingsOpen(true)}>
            ⚙ Settings
          </button>
          {auth.enabled && (
            <button className="secondary" onClick={() => void logout()} title={`Signed in as ${auth.user ?? ""}`}>
              ⎋ Log out
            </button>
          )}
        </div>
      </header>

      <main className="layout">
        <BriefList
          briefs={briefs}
          selectedId={selected?.id ?? null}
          loading={briefsLoading}
          error={briefsError}
          onSelect={(b) => void openBrief(b)}
          onRefresh={() => void loadBriefs(false)}
        />
        <BriefReader brief={selected} content={content} loading={contentLoading} error={contentError} />
        <ChatPanel currentBrief={selected} allBriefs={briefs} />
      </main>

      {settingsOpen && (
        <SettingsModal
          onClose={() => setSettingsOpen(false)}
          onChanged={() => void loadBriefs(false)}
        />
      )}
    </div>
  );
}
